import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button, Modal, StatusHint } from '@indipia/ui-core';
import { ShareHistoryScreen, ShareStateBadge } from '@indipia/shared-features';
import type { FetchSharePage, ShareTypeOption } from '@indipia/shared-features';
import { worklogApi } from '../api/worklogApi';
import { useWorklogStore } from '../store/worklogStore';
import { useHolidays } from '../hooks/useHolidays';
import { ThumbImg } from '../components/ThumbImg';
import { CUST_REQ_SHARED_LABEL, CUST_REQ_SHARED_SEND_TYPE } from '../utils/custReqSendType';
import { PhotoLightbox } from '../components/PhotoLightbox';
import type { ShareHistoryRecipientInfo, ShareHistoryRow, ShareRevokeImpact } from '../types/worklog.types';

/** 상세 펼침 사진 표시 상한 — 7장+더보기(화면기획서 "외부발송이력 조회" DEV_RULES). */
const MAX_THUMBS = 7;

const FAILED_RESULTS = new Set(['FAILED', 'DEAD']);

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 현장리포트 상세 "발송완료" 배지 — MM.DD HH:mm(화면기획서 실측, 연도 생략). */
function fmtBadgeDateTime(iso: string) {
  const d = new Date(iso);
  return `${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// ── 유형 필터(메뉴 고유) — 작업요청·현장리포트, 표준 Badge 색으로 공용 화면이 그린다. ──
const TYPE_OPTIONS: ShareTypeOption[] = [
  { value: 'WORK_ORDER', label: '작업요청', badgeStatus: 'info' },
  { value: 'CUSTOMER_REPORT', label: '현장리포트', badgeStatus: 'accent' },
  { value: CUST_REQ_SHARED_SEND_TYPE, label: CUST_REQ_SHARED_LABEL, badgeStatus: 'warning' },
];

// ── 고객 리포트 행 전용(공유취소·재발송 확인·발송자 미리보기) — 작업요청·고객 요청 추가 전달 행에는 적용하지 않는다(결정 50). ──
const SHARE_MENU_LABEL: Record<string, string> = {
  SCHEDULE: '스케줄 공유', PHOTO: '사진 공유', WORKLOG: '공사현황 공유', WORK_LOG: '공사현황 공유',
};

/** 알림톡 발송 가능 기간 경과(서버 SHARE_SEND_WINDOW_CLOSED) 안내 — 스케줄 공유 이력과 같은 문구 */
const SEND_WINDOW_CLOSED_NOTICE = '공사 종료일에서 7일이 지나 알림톡은 보낼 수 없어요. 링크를 복사해 직접 보내 주세요.';
const NO_SENDABLE_REPORT_NOTICE = '보낼 수 있는 작업보고가 없어요.';

/** 서버 resendBlockedReason → 안내 문구 */
const RESEND_BLOCKED_HINT: Record<string, string> = {
  NOT_SUPPORTED: '재발송할 수 없는 발송 건이에요.',
  REVOKED: '공유가 취소되어 재발송할 수 없어요.',
  SEND_WINDOW_CLOSED: SEND_WINDOW_CLOSED_NOTICE,
  EXPIRED: '링크가 만료되어 재발송할 수 없어요.',
};

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** 영향 조회 결과 → 모달 안내 문장(정보 제공만, 취소를 막지 않는다) */
function impactLines(d: ShareRevokeImpact): string[] {
  const lines: string[] = [];
  if (!d.linkActive) lines.push('이미 취소되었거나 만료된 링크예요.');
  if (d.sameLinkHistoryCount > 1) lines.push(`같은 링크로 보낸 발송 ${d.sameLinkHistoryCount}건이 모두 열 수 없게 돼요.`);
  for (const m of d.otherMenus ?? []) {
    if (m.historyCount > 0) lines.push(`${SHARE_MENU_LABEL[m.menu] ?? m.menu}에서 같은 링크로 보낸 ${m.historyCount}건도 열 수 없게 돼요.`);
  }
  if (d.viewCount > 0) {
    lines.push(`이 링크는 ${d.viewCount}번 열렸어요${d.lastViewedDtm ? ` (마지막 ${fmtDateTime(d.lastViewedDtm)})` : ''}.`);
  }
  return lines;
}

type ImpactState = { status: 'loading' } | { status: 'ready'; data: ShareRevokeImpact } | { status: 'error' };

function apiErrorInfo(err: unknown): { status?: number; code?: string; message?: string } {
  const r = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response;
  return { status: r?.status, code: r?.data?.code, message: r?.data?.message };
}

/** 발송자 미리보기(새 창) — 로그인 토큰으로 열며 열람 기록이 남지 않는다. 같은 링크로 여러 번 보낸 경우 구분하도록 발송 이력 번호(historyId)를 싣는다. */
function buildReportPreviewUrl(sendHistId: number): string {
  return `${window.location.origin}/agents/work-log/customer-reports/preview?historyId=${sendHistId}`;
}

export function ShareHistoryPage() {
  const siteOptions = useWorklogStore((s) => s.siteOptions);
  const holidays = useHolidays();
  // 상세 바깥(모달 확인)에서 쓰는 재조회 — 상세가 렌더될 때마다 최신 reload 를 보관한다.
  const reloadRef = useRef<() => void>(() => {});

  // ── 어댑터 — sv-worklog /share-history 응답을 공용 ShareHistoryRow(수신자 1명 구조)로 정규화 ──
  const fetchPage = useCallback<FetchSharePage>(async (query, signal) => {
    const res = await worklogApi.getShareHistory(
      {
        siteId: query.siteId,
        type: query.typeCode as 'WORK_ORDER' | 'CUSTOMER_REPORT' | 'CUST_REQ_SHARED' | undefined,
        viewStatus: query.viewStatus,
        keyword: query.keyword,
        dateFrom: query.dateFrom,
        dateTo: query.dateTo,
        page: query.page,
        size: query.size,
        sort: query.sortDesc ? '-sendDtm' : 'sendDtm',
      },
      signal,
    );
    const data = res.data.data;
    return {
      hasNext: data.hasNext,
      // 서버 목록엔 revokedYn·expiresDtm·shareId 가 없고 shareState(NORMAL|REVOKED|EXPIRED)만 온다 → expiresAt·shareId 는 null.
      // 고객 리포트 행만 공유취소·재발송 확인·미리보기를 상세에서 처리한다(목록 행 [재발송]은 숨김). 작업요청·고객 요청 추가 전달 행은 기존 그대로.
      items: data.items.map((row) => {
        const isReport = row.type === 'CUSTOMER_REPORT';
        const state = row.shareState ?? 'NORMAL';
        return {
          id: row.sendHistId,
          typeCode: row.type,
          siteName: row.siteName,
          sentAt: row.sentAt,
          expiresAt: null,
          resendable: isReport ? false : row.resendable,
          revokable: isReport && state === 'NORMAL',
          raw: row,
          recipients: [
            {
              historyId: row.sendHistId,
              name: row.recipientName ?? '-',
              role: row.recipientRole === 'CC' ? '참조' : null,
              phone: row.recipientPhone,
              channel: row.channelType,
              viewed: row.viewed,
              viewedAt: row.viewedAt,
              shareState: state,
              shareId: null,
              previewUrl: isReport ? buildReportPreviewUrl(row.sendHistId) : null,
              resendable: row.resendable,
            },
          ],
        };
      }),
    };
  }, []);

  // ── 공유취소 확인 모달 — 영향을 보여 주되 취소를 막지 않는다(고객 리포트 행 전용) ──
  const [revokeTarget, setRevokeTarget] = useState<ShareHistoryRow | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [impact, setImpact] = useState<ImpactState>({ status: 'loading' });

  // 모달이 열리면 영향을 조회한다 — 조회가 실패해도 영향 표시만 빠지고 취소는 계속할 수 있다.
  useEffect(() => {
    if (!revokeTarget) return;
    const controller = new AbortController();
    setImpact({ status: 'loading' });
    worklogApi
      .getShareRevokeImpact(revokeTarget.sendHistId, controller.signal)
      .then((res) => setImpact({ status: 'ready', data: res.data.data }))
      .catch((err: { name?: string; code?: string }) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') return;
        setImpact({ status: 'error' });
      });
    return () => controller.abort();
  }, [revokeTarget]);

  const closeRevoke = () => { if (!revoking) setRevokeTarget(null); };
  const confirmRevoke = async () => {
    const target = revokeTarget;
    if (!target || revoking) return;
    setRevoking(true);
    try {
      await worklogApi.revokeShare(target.sendHistId);
      toast.success('공유를 취소했습니다.');
      setRevokeTarget(null);
      reloadRef.current();
    } catch (err) {
      if (apiErrorInfo(err).status === 409) {
        toast.error('이미 취소된 링크입니다.');
        setRevokeTarget(null);
        reloadRef.current();
      } else {
        toast.error('공유 취소 중 오류가 발생했습니다.');
      }
    } finally {
      setRevoking(false);
    }
  };

  // ── 재발송 확인 모달(고객 리포트 행 전용) — 서버 resendable 이 true 인 건만 열린다 ──
  const [resendTarget, setResendTarget] = useState<ShareHistoryRow | null>(null);
  const [resending, setResending] = useState(false);
  const closeResend = () => { if (!resending) setResendTarget(null); };
  const confirmResend = async () => {
    const target = resendTarget;
    if (!target || resending) return;
    setResending(true);
    try {
      const res = await worklogApi.resendShare(target.sendHistId);
      const result = res.data.data?.sendResult;
      if (result != null && FAILED_RESULTS.has(result)) {
        toast.error('재발송에 실패했어요. 잠시 후 다시 시도해 주세요.');
      } else {
        setResendTarget(null);
        toast.success('재발송 알림톡을 발송했습니다.');
      }
    } catch (err) {
      const info = apiErrorInfo(err);
      if (info.code === 'SHARE_SEND_WINDOW_CLOSED') {
        setResendTarget(null);
        toast.error(SEND_WINDOW_CLOSED_NOTICE);
      } else if (info.code === 'NO_SENDABLE_REPORT') {
        setResendTarget(null);
        toast.error(NO_SENDABLE_REPORT_NOTICE);
      } else {
        toast.error(info.message || '재발송 중 오류가 발생했어요.');
      }
    } finally {
      reloadRef.current();
      setResending(false);
    }
  };

  const impactHints = impact.status === 'ready' ? impactLines(impact.data) : [];

  return (
    <>
      <ShareHistoryScreen
        title="공사현황 공유 이력"
        description="시공반장에게 보낸 작업요청과 고객에게 보낸 작업보고(현장리포트)의 열람 여부를 확인하고, 필요 시 재발송하거나 공유를 취소할 수 있습니다."
        typeOptions={TYPE_OPTIONS}
        typeAllLabel="전체 유형"
        siteOptions={siteOptions}
        holidays={holidays}
        fetchPage={fetchPage}
        renderDetail={(row, { reload }) => {
          reloadRef.current = reload;
          return (
            <DetailCard
              row={row.raw as ShareHistoryRow}
              onResend={(r) => setResendTarget(r)}
              onRevoke={(r) => setRevokeTarget(r)}
            />
          );
        }}
        actions={{
          onResend: async (row) => {
            await worklogApi.resendShare(Number(row.id));
          },
        }}
      />

      {/* 공유취소 확인 모달 */}
      <Modal
        open={revokeTarget != null}
        onClose={closeRevoke}
        title="공유취소"
        footerAlign="stretch"
        footer={
          <>
            <Button variant="secondary" disabled={revoking} onClick={closeRevoke}>닫기</Button>
            <Button variant="danger" loading={revoking} onClick={() => void confirmRevoke()}>공유취소</Button>
          </>
        }
      >
        {revokeTarget && (
          <div className="flex flex-col">
            <p className="text-body-sm text-text">
              <b>{revokeTarget.recipientName ?? '고객'}</b>님께 보낸 링크를 취소하면 받은 분은 더 이상 열 수 없어요.
            </p>
            <div className="mt-1 flex flex-col gap-1">
              {impact.status === 'loading' && <StatusHint tone="muted">영향 범위를 확인하고 있어요.</StatusHint>}
              {impact.status === 'error' && <StatusHint tone="muted">영향 범위를 불러오지 못했어요. 취소는 계속할 수 있어요.</StatusHint>}
              {impactHints.map((t) => <StatusHint key={t} tone="warning">{t}</StatusHint>)}
            </div>
          </div>
        )}
      </Modal>

      {/* 재발송 확인 모달 */}
      <Modal
        open={resendTarget != null}
        onClose={closeResend}
        title="재발송 확인"
        footerAlign="stretch"
        footer={
          <>
            <Button variant="secondary" disabled={resending} onClick={closeResend}>취소</Button>
            <Button variant="primary" loading={resending} onClick={() => void confirmResend()}>
              {resending ? '발송 중...' : '재발송하기'}
            </Button>
          </>
        }
      >
        {resendTarget && (
          <div className="flex flex-col">
            <p className="text-body-sm text-text">
              <b>{resendTarget.recipientName ?? '고객'}</b>님께 같은 링크로 작업보고 알림톡을 다시 보내요.
            </p>
            {resendTarget.msgContent && (
              <div className="mt-1 whitespace-pre-line rounded-lg border border-border bg-surface px-05 py-04 text-body-sm text-text">
                {resendTarget.msgContent}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

// ── 상세 카드(.dt-wrap) — 작업요청/현장리포트 각각 전용 섹션 하나만(wo-only) ──
function DetailCard({
  row,
  onResend,
  onRevoke,
}: {
  row: ShareHistoryRow;
  onResend: (row: ShareHistoryRow) => void;
  onRevoke: (row: ShareHistoryRow) => void;
}) {
  const failed = row.sendResult != null && FAILED_RESULTS.has(row.sendResult);
  const [photosExpanded, setPhotosExpanded] = useState(false);
  const [lbIdx, setLbIdx] = useState<number | null>(null);

  const photos = row.detail?.photos ?? [];
  const shown = photosExpanded ? photos.length : Math.min(photos.length, MAX_THUMBS);
  const overflow = !photosExpanded && photos.length > shown ? photos.length - (shown - 1) : 0;
  const thumbs = overflow > 0 ? photos.slice(0, shown - 1) : photos.slice(0, shown);

  const isReport = row.type === 'CUSTOMER_REPORT';
  const state = row.shareState ?? 'NORMAL';
  const blockedHint = row.resendBlockedReason ? RESEND_BLOCKED_HINT[row.resendBlockedReason] : null;

  return (
    <div className="rounded-lg border border-border bg-surface-raised p-05">
      {/* 고객 리포트 행 전용 — 링크 상태 배지·재발송·공유취소 */}
      {isReport && (
        <div className="mb-04 flex flex-wrap items-center gap-02">
          {state !== 'NORMAL' && <ShareStateBadge state={state} />}
          {row.resendable && (
            <Button variant="secondary" size="sm" onClick={() => onResend(row)} className="whitespace-nowrap">
              재발송
            </Button>
          )}
          {state === 'NORMAL' && (
            <Button variant="etc" size="sm" onClick={() => onRevoke(row)} className="whitespace-nowrap">
              공유취소
            </Button>
          )}
          {!row.resendable && blockedHint && <StatusHint tone="muted">{blockedHint}</StatusHint>}
        </div>
      )}
      {row.type === 'WORK_ORDER' || row.type === CUST_REQ_SHARED_SEND_TYPE ? (
        <>
          <SectionTitle icon={<ClipboardIcon />}>{row.type === CUST_REQ_SHARED_SEND_TYPE ? CUST_REQ_SHARED_LABEL : '작업요청 내용'}</SectionTitle>
          <KvGrid>
            <KvRow label="작업공종">
              <span className="inline-block rounded-sm bg-surface px-03 py-01 text-caption font-bold text-text-secondary">
                {row.detail?.workTypeName ?? '-'}
              </span>
            </KvRow>
            <KvRow label="작업자"><RecipientList list={row.detail?.workers} /></KvRow>
            <KvRow label="참조인"><RecipientList list={row.detail?.ccs} /></KvRow>
          </KvGrid>
          <div className="mt-04">
            <div className="text-caption font-bold text-text-muted">요청내역</div>
            <div className="mt-01 whitespace-pre-line text-body-sm text-text">{row.detail?.orderContent ?? '-'}</div>
          </div>
        </>
      ) : (
        <>
          <SectionTitle icon={<ReportIcon />}>
            현장리포트 내용{' '}
            {failed ? (
              <span className="ml-03 inline-flex items-center gap-xs whitespace-nowrap rounded-full px-03 py-01 text-caption font-bold" style={{ background: 'rgba(239,68,68,.07)', color: 'var(--color-error)' }}>
                발송실패 · {row.failReason ?? ''}
              </span>
            ) : (
              <span className="ml-03 inline-flex items-center gap-xs whitespace-nowrap rounded-full px-03 py-01 text-caption font-bold" style={{ background: 'var(--color-successSubtle)', color: 'var(--color-successStrong)' }}>
                <LockIcon />
                발송완료 · {fmtBadgeDateTime(row.sentAt)}
              </span>
            )}
          </SectionTitle>
          <KvGrid>
            <KvRow label="고객명">{row.recipientName ?? '-'}</KvRow>
            <KvRow label="전화번호">{row.recipientPhone ?? '-'}</KvRow>
          </KvGrid>
          <div className="mt-04 text-caption font-bold" style={{ color: 'var(--indipia-purple-500)' }}>
            고객님께 전하는 메시지
          </div>
          <div className="mt-02 whitespace-pre-line rounded-lg border border-border bg-surface px-05 py-04 text-body-sm text-text">
            {row.detail?.customerMemo ?? '-'}
          </div>
        </>
      )}

      <div className="mt-04">
        <div className="text-caption font-bold text-text-muted">
          첨부사진 <span>{photos.length}장</span>
        </div>
        {photos.length === 0 ? (
          <div className="mt-02 text-body-sm text-text-muted">첨부사진 없음</div>
        ) : (
          <div className="mt-02">
            <div className="flex flex-wrap gap-02">
              {thumbs.map((p, i) => (
                <button
                  key={p.photoRefId}
                  type="button"
                  aria-label={`사진 ${i + 1} 크게보기`}
                  onClick={() => setLbIdx(i)}
                  className="h-16 w-16 overflow-hidden rounded-md border border-border p-0"
                >
                  <ThumbImg url={p.thumbUrl} className="h-full w-full object-cover" />
                </button>
              ))}
              {overflow > 0 && (
                <button
                  type="button"
                  aria-label="사진 더보기"
                  onClick={() => setPhotosExpanded(true)}
                  className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed text-body-sm font-bold"
                  style={{ borderColor: 'var(--status-info-200)', background: 'var(--color-infoSubtle)', color: 'var(--color-info)' }}
                >
                  +{overflow}
                </button>
              )}
            </div>
            {photosExpanded && photos.length > MAX_THUMBS && (
              <button type="button" onClick={() => setPhotosExpanded(false)} className="mt-02 text-caption font-bold text-group">
                접기
              </button>
            )}
          </div>
        )}
      </div>

      <PhotoLightbox
        photos={photos.flatMap((p) => (p.url ? [{ url: p.url }] : []))}
        index={lbIdx}
        onClose={() => setLbIdx(null)}
        onIndexChange={setLbIdx}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />
    </div>
  );
}

function SectionTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-04 flex items-center gap-sm text-body-sm font-bold text-text">
      {icon}
      {children}
    </div>
  );
}

/** kv 그리드 — 라벨열 고정폭(72px)·값열 가변(화면기획서 .kv 실측). */
function KvGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid items-center gap-x-md gap-y-sm" style={{ gridTemplateColumns: '72px 1fr' }}>{children}</div>;
}

function KvRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="text-caption font-bold text-text-muted">{label}</div>
      <div className="text-body-sm text-text">{children}</div>
    </>
  );
}

function RecipientList({ list }: { list: ShareHistoryRecipientInfo[] | null | undefined }) {
  if (!list || list.length === 0) return <>-</>;
  return (
    <>
      {list.map((r, i) => (
        <span key={i}>
          {i > 0 && ', '}
          {r.name}
          {r.phone && <span className="ml-03 text-caption text-text-muted">{r.phone}</span>}
        </span>
      ))}
    </>
  );
}

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <polyline points="22 6 12 13 2 6" />
    </svg>
  );
}
